import { useQuery } from '@tanstack/react-query';
import { EmpButton as Button, EmpSpace as Space, EmpTag as Tag, TileCard } from '@empowernow/ui';
import PageHeading from '../core/a11y/PageHeading';
import { workflowApi } from '../core/services/api';
import { useMemo, useState } from 'react';

function matches(s: string, q: string) { return s.toLowerCase().includes(q.toLowerCase()); }

export default function CatalogPage() {
  const wfQ = useQuery({ queryKey: ['catalog.workflows'], queryFn: () => workflowApi.listWorkflows() as any });
  const cmdQ = useQuery({ queryKey: ['catalog.commands'], queryFn: () => workflowApi.browseCatalog() as any });
  const [q, setQ] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const workflows = useMemo(() => {
    const data = wfQ.data as any;
    const raw = (data?.data?.items || data?.items || []) as Array<any>;
    if (!q) return raw;
    return raw.filter((it: any) => matches(it.name || '', q) || matches(it.id || '', q));
  }, [wfQ.data, q]);

  const commands = useMemo(() => {
    const payload: any = cmdQ.data?.data ?? cmdQ.data; // tolerate axios/standard
    const systems = Array.isArray(payload?.systems) ? payload.systems : [];
    const flat = systems.flatMap((s: any) => (s.objects || []).flatMap((o: any) => (o.actions || []).map((act: string) => ({
      system: s.name,
      objectType: o.name,
      action: act,
      key: `${s.name}.${o.name}.${act}`,
    }))));
    if (!q) return flat;
    return flat.filter((it: any) => matches(it.system, q) || matches(it.objectType, q) || matches(it.action, q));
  }, [cmdQ.data, q]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageHeading title="Catalog">Catalog</PageHeading>
        <Space>
          <input placeholder="Search" value={q} onChange={(e: any) => setQ(e.target.value)} />
          <Button onClick={() => { wfQ.refetch(); cmdQ.refetch(); }}>Refresh</Button>
        </Space>
      </div>

      {/* Workflows section */}
      <TileCard theme="auto" elevation={1}>
        <div style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Workflows</div>
          {wfQ.isLoading && <div role="status" aria-live="polite">Loading…</div>}
          {wfQ.error && <div role="alert">Failed to load workflows</div>}
          {!wfQ.isLoading && !wfQ.error && workflows.length === 0 && (
            <div>No workflows match your filter.</div>
          )}
          {!wfQ.isLoading && !wfQ.error && workflows.map((it: any) => (
            <TileCard key={it.id || it.name} theme="auto" elevation={1}>
              <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{it.name || it.id}</div>
                  {it.system && <div style={{ color: '#666' }}>{it.system}</div>}
                </div>
                <Space>
                  <Button size="small" href={`/run/workflow/${it.id || ''}`}>Open</Button>
                  {it.sample?.curl && (
                    <Button size="small" onClick={async () => {
                      try { await navigator.clipboard.writeText(it.sample.curl); setCopiedId(it.id || it.name); setTimeout(() => setCopiedId(null), 1200); } catch {}
                    }}>Copy cURL</Button>
                  )}
                  {copiedId && (copiedId === (it.id || it.name)) && <Tag color="green">Copied</Tag>}
                </Space>
              </div>
            </TileCard>
          ))}
        </div>
      </TileCard>

      {/* Commands section */}
      <TileCard theme="auto" elevation={1}>
        <div style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Commands</div>
          {cmdQ.isLoading && <div role="status" aria-live="polite">Loading…</div>}
          {cmdQ.error && <div role="alert">Failed to load commands</div>}
          {!cmdQ.isLoading && !cmdQ.error && commands.length === 0 && (
            <div>No commands match your filter.</div>
          )}
          {!cmdQ.isLoading && !cmdQ.error && commands.map((it: any) => (
            <TileCard key={it.key} theme="auto" elevation={1}>
              <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{it.system}.{it.objectType}.{it.action}</div>
                  <div style={{ color: '#666' }}>{it.system} / {it.objectType}</div>
                </div>
                <Space>
                  <Button size="small" href={`/form?system=${encodeURIComponent(it.system)}&object_type=${encodeURIComponent(it.objectType)}&action=${encodeURIComponent(it.action)}`}>Open</Button>
                </Space>
              </div>
            </TileCard>
          ))}
        </div>
      </TileCard>
    </div>
  );
}


